import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, goToAppStore } from "../lib/api";

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get(`/articles/${slug}`)
      .then((r) => live && setArticle(r.data))
      .catch(() => live && setNotFound(true));
    return () => {
      live = false;
    };
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Senior Stylist`;
    }
  }, [article]);

  useEffect(() => {
    if (notFound) navigate("/", { replace: true });
  }, [notFound, navigate]);

  if (!article) {
    return (
      <div className="min-h-screen bg-stylist-bg text-stylist-ink">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-40 text-center text-stylist-muted font-mono text-sm">
          Loading…
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stylist-bg text-stylist-ink">
      <Navbar />
      <article className="pt-28 pb-20" data-testid="article-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <Link
            to="/journal"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 text-sm text-stylist-muted hover:text-stylist-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> The Style Journal
          </Link>

          <span className="eyebrow mt-8 block">{article.category}</span>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl leading-tight text-stylist-ink">
            {article.title}
          </h1>

          <div className="mt-6 flex items-center gap-4 pb-8 border-b border-stylist-border">
            <img
              src={article.author_avatar}
              alt={article.author}
              className="w-11 h-11 rounded-full object-cover"
            />
            <div>
              <p className="text-sm font-medium text-stylist-ink">{article.author}</p>
              <p className="text-xs text-stylist-muted font-mono flex items-center gap-2">
                {article.date} <span>·</span>
                <Clock className="w-3.5 h-3.5" /> {article.read_time}
              </p>
            </div>
          </div>

          <img
            src={article.image}
            alt={article.title}
            className="mt-8 w-full rounded-xl object-cover aspect-video"
          />

          <div className="mt-10 space-y-6">
            {article.body.map((p, i) => (
              <p key={i} className="text-[18px] leading-[1.85] text-stylist-ink2">
                {p}
              </p>
            ))}
          </div>

          {article.quote && (
            <blockquote className="my-12 border-l-2 border-stylist-gold pl-6 py-2">
              <p className="font-serif text-2xl md:text-3xl italic text-stylist-ink leading-snug">
                “{article.quote}”
              </p>
            </blockquote>
          )}

          <div className="mt-12 rounded-2xl bg-stylist-ink text-white p-8 text-center">
            <h3 className="font-serif text-2xl">Want advice tailored to your hair?</h3>
            <p className="mt-3 text-white/70 text-sm max-w-md mx-auto">
              Message a certified senior stylist for personalised guidance. A consultation lasts a
              maximum of 10 minutes, most just a few minutes.
            </p>
            <button
              onClick={goToAppStore}
              data-testid="article-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-8 py-3.5 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Get the app <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}
